import {
  type ContextFileEntry,
  type ContextFileStatus,
  type ContextFileTypeTag,
  type FileVersionEntry,
  type OrgAdminFile,
} from '../components/projects/types';
import { ProjectRole } from '../roles';

export interface ApiProject {
  id: string | number;
  name: string;
  description?: string;
  organizationId?: string | number;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  // Effective role of the requesting user for this project (CB25SES-797).
  role?: string;
  // Resolved from projects.owner_id on the backend (CB25SES-1769). Can differ
  // from createdBy after an ownership transfer.
  ownerId?: string;
  ownerName?: string;
}

export interface ListProjectsResponse {
  projects?: ApiProject[];
  total?: string | number;
}

export interface ProjectResponse {
  project?: ApiProject;
}

export type ProjectsApiOptions = {
  baseUrl: string;
  token?: string;
};

export interface ListProjectsParams {
  page?: number;
  pageSize?: number;
}

type RequestOptions = {
  body?: unknown;
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
};

export function mapUserRole(backendRole?: string): ProjectRole | undefined {
  if (!backendRole) return undefined;
  switch (backendRole.toLowerCase()) {
    case 'owner':
      return 'Owner';
    case 'maintainer':
      return 'Maintainer';
    case 'collaborator':
    case 'contributor':
      return 'Collaborator';
    case 'viewer':
      return 'Viewer';
    default:
      return undefined;
  }
}

export class ProjectsApiError extends Error {
  constructor(readonly status: number) {
    super(`Projects API request failed with status ${status}`);
    this.name = 'ProjectsApiError';
  }
}

export function isForbiddenError(error: unknown): boolean {
  return error instanceof ProjectsApiError && error.status === 403;
}

async function request<T>(
  options: ProjectsApiOptions,
  path: string,
  requestOptions: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  if (options.token) {
    headers.Authorization = `Bearer ${options.token}`;
  }

  let body: string | undefined;
  if (requestOptions.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(requestOptions.body);
  }

  const res = await fetch(`${options.baseUrl}${path}`, {
    method: requestOptions.method ?? 'GET',
    headers,
    body,
    // Session lives in the backend's HttpOnly cookie (CB25SES-1198/1206).
    // `options.token` above remains supported for callers that already
    // have an explicit bearer token, but the cookie is sent either way.
    credentials: 'include',
  });

  if (!res.ok) {
    throw new ProjectsApiError(res.status);
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

export async function listProjects(
  options: ProjectsApiOptions,
  params?: ListProjectsParams,
) {
  const queryParams = new URLSearchParams();
  if (params?.page) {
    queryParams.append('page', String(params.page));
  }
  if (params?.pageSize) {
    queryParams.append('page_size', String(params.pageSize));
  }

  const path = queryParams.toString()
    ? `/projects?${queryParams.toString()}`
    : '/projects';

  return request<ListProjectsResponse>(options, path);
}

export async function createProject(
  options: ProjectsApiOptions,
  input: { description: string; name: string },
) {
  return request<ProjectResponse>(options, '/projects', {
    method: 'POST',
    body: input,
  });
}

export async function updateProject(
  options: ProjectsApiOptions,
  projectId: string,
  input: { description?: string; name?: string },
) {
  return request<ProjectResponse>(options, `/projects/${projectId}`, {
    method: 'PATCH',
    body: input,
  });
}

export async function deleteProjectById(
  options: ProjectsApiOptions,
  projectId: string,
) {
  return request<{ deleted: boolean; projectId: string | number }>(
    options,
    `/projects/${projectId}`,
    { method: 'DELETE' },
  );
}

export interface UploadContextFileResponse {
  fileId: string;
  status: ContextFileStatus;
}

export async function uploadContextFile(
  options: ProjectsApiOptions,
  projectId: string,
  file: File,
  typeTag: ContextFileTypeTag,
  onProgress?: (percent: number) => void,
): Promise<UploadContextFileResponse> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const arrayBuffer = reader.result as ArrayBuffer;
      const bytes = new Uint8Array(arrayBuffer);
      // Process in chunks to avoid call stack size limit for large files (>250KB)
      const CHUNK = 8192;
      let binary = '';
      for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
      }
      const base64 = btoa(binary);

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (options.token) {
        headers.Authorization = `Bearer ${options.token}`;
      }

      const xhr = new XMLHttpRequest();
      xhr.open(
        'POST',
        `${options.baseUrl}/projects/${projectId}/knowledge-files`,
      );
      // Session lives in the backend's HttpOnly cookie (CB25SES-1198/1206).
      xhr.withCredentials = true;
      Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));

      if (onProgress) {
        xhr.upload.onprogress = event => {
          if (event.lengthComputable) {
            onProgress(Math.round((event.loaded / event.total) * 100));
          }
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText) as {
              id: string;
              status: string;
            };
            resolve({
              fileId: data.id,
              status: data.status as ContextFileStatus,
            });
          } catch {
            reject(new Error('Failed to parse upload response'));
          }
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error('Upload request failed'));
      xhr.send(
        JSON.stringify({
          file_name: file.name,
          content_type: file.type || 'application/octet-stream',
          file_content: base64,
          type_tag: typeTag,
        }),
      );
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

export async function listContextFiles(
  options: ProjectsApiOptions,
  projectId: string,
): Promise<ContextFileEntry[]> {
  const res = await request<{ files?: ContextFileEntry[] }>(
    options,
    `/projects/${projectId}/knowledge-files`,
  );
  return res.files ?? [];
}

// GET /projects/{projectId}/knowledge-files/{fileId} (CB25SES-1091)
export async function getKnowledgeFile(
  options: ProjectsApiOptions,
  projectId: string,
  fileId: string,
): Promise<ContextFileEntry> {
  const res = await request<{ file: ContextFileEntry }>(
    options,
    `/projects/${projectId}/knowledge-files/${fileId}`,
  );
  return res.file;
}

interface RawFileVersion {
  versionNumber: number;
  status: string;
  // int64 fields are serialized as decimal strings by protojson.
  uploadedBy?: string | number;
  // Empty when the uploader's user record no longer exists.
  uploaderName?: string;
  uploadedAt: string;
}

function mapRawVersion(raw: RawFileVersion): FileVersionEntry {
  return {
    versionNumber: raw.versionNumber,
    status: raw.status as ContextFileStatus,
    uploadedBy:
      raw.uploadedBy !== undefined
        ? {
            userId: Number(raw.uploadedBy),
            displayName: raw.uploaderName ?? '',
          }
        : null,
    uploadedAt: raw.uploadedAt,
  };
}

// GET /projects/{projectId}/knowledge-files/{fileId}/versions (CB25SES-1092)
export async function listFileVersions(
  options: ProjectsApiOptions,
  projectId: string,
  fileId: string,
): Promise<FileVersionEntry[]> {
  const res = await request<{ versions?: RawFileVersion[] }>(
    options,
    `/projects/${projectId}/knowledge-files/${fileId}/versions`,
  );
  return (res.versions ?? []).map(mapRawVersion);
}

export interface UploadFileVersionResponse {
  fileId: string;
  versionNumber: number;
  status: ContextFileStatus;
}

// POST /projects/{projectId}/knowledge-files/{fileId}/versions (CB25SES-1092)
// No type_tag: a version doesn't carry one, only the parent file does.
export async function uploadFileVersion(
  options: ProjectsApiOptions,
  projectId: string,
  fileId: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<UploadFileVersionResponse> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const arrayBuffer = reader.result as ArrayBuffer;
      const bytes = new Uint8Array(arrayBuffer);
      const CHUNK = 8192;
      let binary = '';
      for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
      }
      const base64 = btoa(binary);

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (options.token) {
        headers.Authorization = `Bearer ${options.token}`;
      }

      const xhr = new XMLHttpRequest();
      xhr.open(
        'POST',
        `${options.baseUrl}/projects/${projectId}/knowledge-files/${fileId}/versions`,
      );
      xhr.withCredentials = true;
      Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));

      if (onProgress) {
        xhr.upload.onprogress = event => {
          if (event.lengthComputable) {
            onProgress(Math.round((event.loaded / event.total) * 100));
          }
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText) as {
              fileId: string;
              versionNumber: number;
              status: string;
            };
            resolve({
              fileId: data.fileId,
              versionNumber: data.versionNumber,
              status: data.status as ContextFileStatus,
            });
          } catch {
            reject(new Error('Failed to parse upload response'));
          }
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error('Upload request failed'));
      xhr.send(
        JSON.stringify({
          file_name: file.name,
          content_type: file.type || 'application/octet-stream',
          file_content: base64,
        }),
      );
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

export interface DeleteFileResponse {
  fileId: string;
  deleted: boolean;
}

// DELETE /projects/{projectId}/knowledge-files/{fileId} (CB25SES-1093)
export async function deleteKnowledgeFile(
  options: ProjectsApiOptions,
  projectId: string,
  fileId: string,
): Promise<DeleteFileResponse> {
  return request<DeleteFileResponse>(
    options,
    `/projects/${projectId}/knowledge-files/${fileId}`,
    { method: 'DELETE' },
  );
}

export async function includeOrgFile(
  options: ProjectsApiOptions,
  projectId: string,
  fileId: string,
): Promise<void> {
  return request<void>(options, `/projects/${projectId}/context/${fileId}`, {
    method: 'POST',
  });
}

export async function excludeOrgFile(
  options: ProjectsApiOptions,
  projectId: string,
  fileId: string,
): Promise<void> {
  return request<void>(options, `/projects/${projectId}/context/${fileId}`, {
    method: 'DELETE',
  });
}

// DELETE /organizations/{orgId}/files/{fileId} (CB25SES-1093)
export async function deleteOrgFile(
  options: ProjectsApiOptions,
  orgId: string,
  fileId: string,
): Promise<DeleteFileResponse> {
  return request<DeleteFileResponse>(
    options,
    `/organizations/${orgId}/files/${fileId}`,
    { method: 'DELETE' },
  );
}

export interface ContextUsageProject {
  projectId: number;
  projectName: string;
}

// GET /organizations/{orgId}/files/{fileId}/context-usage (CB25SES-1093)
// Org-scoped files only — project-scoped files 404 here since they can't be
// shared across projects, so there's nothing to warn about.
export async function getFileContextUsage(
  options: ProjectsApiOptions,
  orgId: string,
  fileId: string,
): Promise<ContextUsageProject[]> {
  const res = await request<{ projects?: ContextUsageProject[] }>(
    options,
    `/organizations/${orgId}/files/${fileId}/context-usage`,
  );
  return res.projects ?? [];
}

export interface UploadOrgAdminFileResponse {
  fileId: string;
  versionId: string;
  status: ContextFileStatus;
  isDefault: boolean;
}

// An organization file as the backend lists it (api.organizations.v1
// OrganizationFile, camelCase).
interface RawOrgFile {
  id: string;
  name: string;
  fileTypeTag: string;
  isDefault?: boolean;
  status: string;
  // int64 fields are serialized as decimal strings by protojson.
  versionNumber?: string | number;
  errorReport?: { stage?: string; message?: string } | null;
}

function mapRawOrgFile(raw: RawOrgFile): OrgAdminFile {
  return {
    id: raw.id,
    name: raw.name,
    fileTypeTag: raw.fileTypeTag as ContextFileTypeTag,
    status: raw.status as ContextFileStatus,
    isDefault: raw.isDefault ?? false,
    version: Number(raw.versionNumber ?? 1),
    // The backend sends only the uploader's id, no name to show.
    uploader: null,
    errorReport: raw.errorReport?.message ?? null,
  };
}

// GET /organizations/{orgId}/files
export async function listOrgAdminFiles(
  options: ProjectsApiOptions,
  orgId: string,
): Promise<OrgAdminFile[]> {
  const res = await request<{ files?: RawOrgFile[] }>(
    options,
    `/organizations/${orgId}/files`,
  );
  return (res.files ?? []).map(mapRawOrgFile);
}

// POST /organizations/{orgId}/files (CB25SES-1079)
// file_tags is a string array; type tag is sent as a single-element array.
export async function uploadOrgAdminFile(
  options: ProjectsApiOptions,
  orgId: string,
  file: File,
  typeTag: ContextFileTypeTag,
  isDefault: boolean,
  onProgress?: (percent: number) => void,
): Promise<UploadOrgAdminFileResponse> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const arrayBuffer = reader.result as ArrayBuffer;
      const bytes = new Uint8Array(arrayBuffer);
      // Spread into String.fromCharCode overflows the call stack for large files;
      // chunked reduce avoids the limit while staying synchronous.
      const CHUNK = 8192;
      let binary = '';
      for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
      }
      const base64 = btoa(binary);

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (options.token) {
        headers.Authorization = `Bearer ${options.token}`;
      }

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${options.baseUrl}/organizations/${orgId}/files`);
      // Session lives in the backend's HttpOnly cookie (CB25SES-1198/1206).
      xhr.withCredentials = true;
      Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));

      if (onProgress) {
        xhr.upload.onprogress = event => {
          if (event.lengthComputable) {
            onProgress(Math.round((event.loaded / event.total) * 100));
          }
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText) as {
              fileId: string;
              versionId: string;
              status: string;
              isDefault: boolean;
            };
            resolve({
              fileId: data.fileId,
              versionId: data.versionId,
              status: data.status as ContextFileStatus,
              isDefault: data.isDefault,
            });
          } catch {
            reject(new Error('Failed to parse upload response'));
          }
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error('Upload request failed'));
      xhr.send(
        JSON.stringify({
          file_name: file.name,
          content_type: file.type || 'application/octet-stream',
          file_content: base64,
          file_tags: [typeTag],
          is_default: isDefault,
        }),
      );
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

// Test Case template
export type ProjectTestCaseTemplateSource =
  | 'PROJECT_TEST_CASE_TEMPLATE_SOURCE_UNSPECIFIED'
  | 'PROJECT_TEST_CASE_TEMPLATE_SOURCE_PLATFORM_DEFAULT'
  | 'PROJECT_TEST_CASE_TEMPLATE_SOURCE_CUSTOM';

export interface ProjectTestCaseTemplate {
  source: ProjectTestCaseTemplateSource;
  fileName?: string;
  uploadedAt?: string;
  version?: number;
  templateId?: string;
}

export interface ProjectTestCaseTemplateResponse {
  template?: ProjectTestCaseTemplate;
}

export interface UploadProjectTestCaseTemplateResponse {
  template: ProjectTestCaseTemplate;
}

function arrayBufferToBase64(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';

  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  return btoa(binary);
}

export async function getProjectTestCaseTemplate(
  options: ProjectsApiOptions,
  projectId: string,
): Promise<ProjectTestCaseTemplateResponse> {
  return request<ProjectTestCaseTemplateResponse>(
    options,
    `/projects/${projectId}/test-case-template`,
  );
}

export async function uploadProjectTestCaseTemplate(
  options: ProjectsApiOptions,
  projectId: string,
  file: File,
): Promise<UploadProjectTestCaseTemplateResponse> {
  const arrayBuffer = await file.arrayBuffer();
  const base64 = arrayBufferToBase64(arrayBuffer);

  return request<UploadProjectTestCaseTemplateResponse>(
    options,
    `/projects/${projectId}/test-case-template`,
    {
      method: 'POST',
      body: {
        file_name: file.name,
        content_type: file.type || 'application/octet-stream',
        file_content: base64,
      },
    },
  );
}

export async function revertProjectTestCaseTemplate(
  options: ProjectsApiOptions,
  projectId: string,
  templateId: string,
): Promise<ProjectTestCaseTemplateResponse> {
  return request<ProjectTestCaseTemplateResponse>(
    options,
    `/projects/${projectId}/test-case-template/${templateId}`,
    {
      method: 'DELETE',
    },
  );
}
